import { createStore } from "solid-js/store";
import { isServer } from "solid-js/web";

import { LocalStorageKey } from "@/constants/constants.ts";
import { useUrlStorage } from "@/hooks/useUrlStorage.ts";
import {
  getLocalStorageItem,
  setLocalStorageItem,
} from "@/lib/localStorage.ts";

import { checkMobile, setIsMobileState } from "../lib/isMobile.ts";
import { getQueryParam } from "../lib/urlUtils.ts";
import { getRandomString } from "../lib/utils.ts";

// Types
export type DeviceType = "mobile" | "pc";

// Device Info Type - based on the DeviceHelper class
export interface DeviceInfo {
  device_id: string;
  UA: string;
  mobile: boolean;
  pc: boolean;
  mac: boolean;
  app: boolean; // 官方包【apk或ipa】
  ios: boolean; // ios系统
  iosVersion: number; // ios系统版本
  iosWebView: boolean; // ios webview
  safari: boolean; // safari浏览器
  android: boolean; // 安卓系统
  webview: boolean; // 任意WebView的壳包【包含ios webview】
  pwa: boolean; // 保存到桌面app
  os_type: number; // 系统类型 0-未知 1-PC 2-android 3-ios
  client_type: number; // 登录客户端类型= Web=0 App[or PWA]=1 等
  app_type: number; // 应用类型 0.H5 1.pwa 3.官方App 4.外部App 5.马甲包【上架不稳定包】6.官方新版App 10.RN-App
  m_app: number; // url参数 1.官方pwa 2.外部pwa 3.官方App 4.外部App 5.马甲包【上架不稳定包】 6.官方新版App 10.RN-App
  referrer: string; // 来源header【同源不处理】
  appShell: string; // 应用壳包
}

const [deviceInfo, setDeviceInfo] = createStore<DeviceInfo>({
  device_id: "",
  UA: "",
  mobile: false,
  pc: true,
  mac: false,
  app: false,
  ios: false,
  iosVersion: 0,
  iosWebView: false,
  safari: false,
  android: false,
  webview: false,
  pwa: false,
  os_type: 0,
  client_type: 0,
  app_type: 0,
  m_app: 0,
  referrer: "",
  appShell: "",
});

// Helper functions from DeviceHelper
function isMac(userAgent: string) {
  return (
    // @ts-expect-error - Navigator userAgentData API not fully typed in TypeScript
    navigator?.userAgentData?.platform === "macOS" ||
    /Macintosh/i.test(userAgent)
  );
}

function isIosWebView(userAgent: string, isIosDevice: boolean) {
  if (isIosDevice) {
    return /(iPhone|iPod|iPad).*AppleWebKit(?!.*Safari)/i.test(userAgent);
  }
  return false;
}

function isAndroid(userAgent: string) {
  return /android\s*(\d+(?:\.\d+)*)/i.test(userAgent);
}

function isIos(userAgent: string) {
  return /(iPad|iPhone|iPod).*OS ((\d+_?){2,3})/i.test(userAgent);
}

function isSafari(userAgent: string) {
  return /^((?!chrome|android).)*safari/i.test(userAgent);
}

function getIosVersion(userAgent: string) {
  if (userAgent.includes("iPhone OS ")) {
    const versionArr = userAgent
      .split("iPhone OS ")[1]
      .split(" ")[0]
      .split("_");
    return Number(versionArr[0] + "." + versionArr[1]);
  }
  return 0;
}

export function getOsType(userAgent: string, isPCDevice: boolean) {
  if (isServer) {
    return 0;
  }
  let os_type = 0;
  if (isAndroid(userAgent)) {
    os_type = 2;
  } else if (isIos(userAgent)) {
    os_type = 3;
  } else if (isPCDevice) {
    os_type = 1;
  }
  return os_type;
}

// 是否主屏幕打开
export function isOpenByStandalone() {
  // @ts-expect-error - Navigator standalone property not included in standard TypeScript types
  if (navigator?.standalone) {
    return true;
  }
  if (
    window &&
    window.matchMedia &&
    typeof window.matchMedia == "function" &&
    window.matchMedia("(display-mode: standalone)") &&
    window.matchMedia("(display-mode: standalone)").matches
  ) {
    return true;
  }
  return false;
}

// 0 :H5,1:官方PWA，2：外部PWA 3：官方App 4 外部App 5 马甲包[上架不稳定包]
function getAppType(userAgent: string, isIosDevice: boolean) {
  const appType = 0;
  if (isOpenByStandalone()) {
    return 1;
  }
  if (userAgent.match(/WebApp/i) != null) {
    return 3;
  }
  if (isIosWebView(userAgent, isIosDevice)) {
    return 4;
  }
  // TODO: 2：外部PWA 5 马甲包[上架不稳定包]

  return appType;
}

// 0 :Web,1:App
function getClientType(userAgent: string, isIosDevice: boolean) {
  const appType = getAppType(userAgent, isIosDevice);
  if (appType < 3) {
    return 0;
  }
  return 1;
}

export function initDeviceInfo() {
  if (isServer) {
    return;
  }

  const userAgent = navigator.userAgent;
  const mobile = checkMobile(userAgent);
  const pc = !mobile;
  const isIosDevice = isIos(userAgent);
  const m_app = Number(getQueryParam("mApp"));
  const iosWebViewValue = isIosWebView(userAgent, isIosDevice);
  const appType = getAppType(userAgent, isIosDevice);

  const newDeviceInfo: DeviceInfo = {
    device_id: "", //TODO 获取设备ID
    UA: userAgent,
    mobile,
    pc,
    mac: isMac(userAgent),
    ios: isIosDevice,
    iosVersion: getIosVersion(userAgent),
    iosWebView: iosWebViewValue,
    safari: isSafari(userAgent),
    android: isAndroid(userAgent),
    webview: false,
    pwa: false,
    os_type: getOsType(userAgent, mobile),
    client_type: getClientType(userAgent, isIosDevice),
    app_type: appType,
    m_app,
    app: false,
    referrer: document.referrer,
    appShell: detectAppShell(userAgent),
  };

  setDeviceInfo(newDeviceInfo);
  setIsMobileState(mobile);
}

// 更新设备信息的函数
const updateDeviceInfo = (info: Partial<DeviceInfo>) => {
  setDeviceInfo((prev) => ({ ...prev, ...info }));
};

// 重置设备信息到初始状态
const resetDeviceInfo = () => {
  setDeviceInfo({
    device_id: "",
    UA: "",
    mobile: false,
    pc: true,
    mac: false,
    app: false,
    ios: false,
    iosVersion: 0,
    iosWebView: false,
    safari: false,
    android: false,
    webview: false,
    pwa: false,
    os_type: 0,
    client_type: 0,
    app_type: 0,
    m_app: 0,
    referrer: "",
  });
};
//获取服务端需要的设备对象
function getDeviceObject(bSave = false) {
  const urlParams = useUrlStorage().getStoredParams();
  if (!deviceInfo.device_id) {
    let device_id =
      urlParams["se"] ||
      urlParams["device_id"] ||
      localStorage.getItem("webDeviceUUID") ||
      getRandomString(32);
    if (urlParams["se"]) {
      device_id = "se-" + urlParams["se"];
    }
    localStorage.setItem("webDeviceUUID", device_id);
    setDeviceInfo({ device_id });
    // eventThinkingdata("ta_new_device", { first_check_id: device_id });
  }
  if (bSave) {
    setLocalStorageItem(LocalStorageKey.ClientDevice, deviceInfo);
  }
  return deviceInfo;
}

function detectAppShell(ua: string): string {
  if (/MicroMessenger/i.test(ua)) return "WeChat"; // 微信
  if (/QQ\//i.test(ua)) return "QQ";
  if (/AlipayClient/i.test(ua)) return "Alipay";
  if (/Weibo/i.test(ua)) return "Weibo";
  if (/DingTalk/i.test(ua)) return "DingTalk";
  if (/toutiao|NewsArticle/i.test(ua)) return "Toutiao";
  if (/douyin|aweme/i.test(ua)) return "Douyin";
  if (/fbav|FBAN|FBAV/i.test(ua)) return "Facebook";
  if (/Twitter/i.test(ua)) return "Twitter";
  if (/Instagram/i.test(ua)) return "Instagram";
  if (/Line/i.test(ua)) return "Line";
  if (/Snapchat/i.test(ua)) return "Snapchat";
  if (/whatsapp/i.test(ua)) return "WhatsApp";
  if (/LinkedIn/i.test(ua)) return "LinkedIn";
  if (/Skype/i.test(ua)) return "Skype";
  if (/Viber/i.test(ua)) return "Viber";
  if (/Pinterest/i.test(ua)) return "Pinterest";
  if (/TikTok/i.test(ua)) return "TikTok";
  return "Browser";
}

export { deviceInfo, updateDeviceInfo, resetDeviceInfo, getDeviceObject };
